/**
 * Mock API client for VocabMaster frontend development.
 * Simulates backend responses without requiring a running backend.
 */

// Mock data storage (in-memory)
let mockData = {
  users: {
    'test-user-123': {
      userId: 'test-user-123',
      email: 'test@example.com',
      name: 'Test User',
      interests: 'programming, music, soccer',
      createdAt: new Date('2025-12-01').toISOString(),
    },
  },
  words: [
    {
      wordId: 'WORD#1734307200000#001',
      userId: 'test-user-123',
      word: 'ephemeral',
      examples: [
        { text: 'Like a soccer match\'s fleeting moments, the beauty of live music is ephemeral.', regenerationCount: 0, isManuallyEdited: false },
        { text: 'In programming, cache data is ephemeral and may be cleared at any time.', regenerationCount: 0, isManuallyEdited: false },
        { text: 'The excitement after scoring a goal is ephemeral but intensely memorable.', regenerationCount: 0, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-15',
      createdAt: new Date('2025-12-15T10:00:00Z').toISOString(),
      updatedAt: new Date('2025-12-15T10:00:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1734307200000#002',
      userId: 'test-user-123',
      word: 'serendipity',
      examples: [
        { text: 'Finding a perfect melody while coding was pure serendipity.', regenerationCount: 1, isManuallyEdited: false },
        { text: 'The serendipity of discovering a new programming technique during a music break.', regenerationCount: 0, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-15',
      createdAt: new Date('2025-12-15T11:30:00Z').toISOString(),
      updatedAt: new Date('2025-12-15T12:00:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1734134400000#003',
      userId: 'test-user-123',
      word: 'resilient',
      examples: [
        { text: 'A resilient codebase can handle errors gracefully.', regenerationCount: 0, isManuallyEdited: true },
        { text: 'The team remained resilient after losing the championship match.', regenerationCount: 0, isManuallyEdited: true },
      ],
      dateAdded: '2025-12-13',
      createdAt: new Date('2025-12-13T09:15:00Z').toISOString(),
      updatedAt: new Date('2025-12-13T14:20:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1734048000000#004',
      userId: 'test-user-123',
      word: 'paradigm',
      examples: [
        { text: 'React introduced a new paradigm for building user interfaces.', regenerationCount: 2, isManuallyEdited: false },
        { text: 'The team adopted a new training paradigm focused on ball possession.', regenerationCount: 0, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-12',
      createdAt: new Date('2025-12-12T16:45:00Z').toISOString(),
      updatedAt: new Date('2025-12-12T17:30:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1733961600000#005',
      userId: 'test-user-123',
      word: 'ubiquitous',
      examples: [
        { text: 'JavaScript has become ubiquitous in modern web development.', regenerationCount: 0, isManuallyEdited: false },
        { text: 'Music streaming services are now ubiquitous among soccer fans.', regenerationCount: 0, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-11',
      createdAt: new Date('2025-12-11T08:00:00Z').toISOString(),
      updatedAt: new Date('2025-12-11T08:00:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1733788800000#006',
      userId: 'test-user-123',
      word: 'ameliorate',
      examples: [
        { text: 'Refactoring can ameliorate code quality over time.', regenerationCount: 0, isManuallyEdited: false },
        { text: 'The new training regime helped ameliorate the team\'s defensive weaknesses.', regenerationCount: 0, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-09',
      createdAt: new Date('2025-12-09T13:20:00Z').toISOString(),
      updatedAt: new Date('2025-12-09T13:20:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1733616000000#007',
      userId: 'test-user-123',
      word: 'juxtapose',
      examples: [
        { text: 'When you juxtapose classical music with modern programming, you find unexpected inspiration.', regenerationCount: 0, isManuallyEdited: false },
        { text: 'It\'s interesting to juxtapose soccer strategies with software architecture patterns.', regenerationCount: 1, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-07',
      createdAt: new Date('2025-12-07T10:10:00Z').toISOString(),
      updatedAt: new Date('2025-12-07T11:00:00Z').toISOString(),
    },
    {
      wordId: 'WORD#1733443200000#008',
      userId: 'test-user-123',
      word: 'meticulous',
      examples: [
        { text: 'Writing meticulous code requires attention to every detail.', regenerationCount: 0, isManuallyEdited: false },
        { text: 'The conductor was meticulous in rehearsing each musical phrase.', regenerationCount: 0, isManuallyEdited: false },
      ],
      dateAdded: '2025-12-05',
      createdAt: new Date('2025-12-05T15:30:00Z').toISOString(),
      updatedAt: new Date('2025-12-05T15:30:00Z').toISOString(),
    },
  ],
};

// Helper to simulate network delay
const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

// Helper to generate unique word ID
const generateWordId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `WORD#${timestamp}#${random}`;
};

// Helper to generate AI examples based on interests
const generateMockExamples = (word, interests) => {
  const interestList = interests
    .split(',')
    .map((i) => i.trim())
    .filter((i) => i);
  const selectedInterests = interestList.slice(0, 2);

  const templates = [
    `The concept of ${word} is important in ${selectedInterests[0] || 'daily life'}.`,
    `Understanding ${word} can enhance your ${selectedInterests[1] || 'knowledge'} skills.`,
    `In ${selectedInterests[0] || 'various contexts'}, ${word} plays a crucial role.`,
  ];

  return templates.map(text => ({
    text,
    regenerationCount: 0,
    isManuallyEdited: false,
  }));
};

// Helper to format date as YYYY-MM-DD
const formatDate = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

class MockApiClient {
  constructor() {
    this.userId = 'test-user-123';
    console.log('🎭 Mock API Client initialized - using local mock data');
  }

  // ========================================
  // User Interests API
  // ========================================

  async getInterests() {
    await delay();
    const user = mockData.users[this.userId];

    if (!user || !user.interests) {
      throw {
        response: {
          status: 404,
          data: { message: 'User interests not found' },
        },
      };
    }

    return {
      userId: user.userId,
      interests: user.interests,
      email: user.email,
      name: user.name,
    };
  }

  async createInterests(interests, email, name) {
    await delay();

    mockData.users[this.userId] = {
      userId: this.userId,
      email,
      name,
      interests,
      createdAt: new Date().toISOString(),
    };

    return {
      userId: this.userId,
      interests,
      email,
      name,
      message: 'Interests created successfully',
    };
  }

  async updateInterests(interests) {
    await delay();

    const user = mockData.users[this.userId];
    if (!user) {
      throw {
        response: {
          status: 404,
          data: { message: 'User not found' },
        },
      };
    }

    user.interests = interests;
    user.updatedAt = new Date().toISOString();

    return {
      userId: this.userId,
      interests,
      message: 'Interests updated successfully',
    };
  }

  async updateProfile(name, interests) {
    await delay();

    const user = mockData.users[this.userId];
    if (!user) {
      throw {
        response: {
          status: 404,
          data: { message: 'User not found' },
        },
      };
    }

    user.name = name;
    user.interests = interests;
    user.updatedAt = new Date().toISOString();

    return {
      userId: this.userId,
      name,
      interests,
      email: user.email,
      message: 'Profile updated successfully',
    };
  }

  // ========================================
  // Words API
  // ========================================

  async getWords(params = {}) {
    await delay();

    let words = mockData.words.filter((w) => w.userId === this.userId);

    // Sort by date descending (newest first)
    words.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Apply date filtering if provided
    if (params.startDate) {
      words = words.filter((w) => w.dateAdded >= params.startDate);
    }
    if (params.endDate) {
      words = words.filter((w) => w.dateAdded <= params.endDate);
    }

    // Apply pagination
    const limit = params.limit || 20;
    const offset = params.offset || 0;
    const paginatedWords = words.slice(offset, offset + limit);

    return {
      items: paginatedWords,
      count: paginatedWords.length,
      total: words.length,
      hasMore: offset + limit < words.length,
      nextOffset: offset + limit < words.length ? offset + limit : null,
    };
  }

  async getWord(wordId) {
    await delay();

    const word = mockData.words.find(
      (w) => w.wordId === wordId && w.userId === this.userId
    );

    if (!word) {
      throw {
        response: {
          status: 404,
          data: { message: 'Word not found' },
        },
      };
    }

    return word;
  }

  async createWord(word, generateExamples = true) {
    await delay(800); // Longer delay to simulate AI generation

    const user = mockData.users[this.userId];
    const interests = user?.interests || 'general topics';

    const now = new Date();
    const newWord = {
      wordId: generateWordId(),
      userId: this.userId,
      word,
      examples: generateExamples
        ? generateMockExamples(word, interests)
        : [],
      dateAdded: formatDate(now),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };

    mockData.words.unshift(newWord); // Add to beginning

    return newWord;
  }

  async updateWord(wordId, data) {
    await delay(data.regenerate ? 800 : 300);

    const wordIndex = mockData.words.findIndex(
      (w) => w.wordId === wordId && w.userId === this.userId
    );

    if (wordIndex === -1) {
      throw {
        response: {
          status: 404,
          data: { message: 'Word not found' },
        },
      };
    }

    const word = mockData.words[wordIndex];
    const now = new Date();

    if (data.regenerate) {
      // Per-example regeneration
      if (data.exampleIndex === undefined || data.exampleIndex === null) {
        throw {
          response: {
            status: 400,
            data: { message: 'exampleIndex is required for regeneration' },
          },
        };
      }

      const exampleIndex = data.exampleIndex;
      if (exampleIndex < 0 || exampleIndex >= word.examples.length) {
        throw {
          response: {
            status: 400,
            data: { message: `Invalid example index: ${exampleIndex}` },
          },
        };
      }

      // Check per-example regeneration limit
      const example = word.examples[exampleIndex];
      if (example.regenerationCount >= 3) {
        throw {
          response: {
            status: 400,
            data: { message: 'Maximum regeneration limit (3) reached for this example' },
          },
        };
      }

      const user = mockData.users[this.userId];
      const interests = user?.interests || 'general topics';

      // Generate new example text
      const templates = [
        `The concept of ${word.word} is important in ${interests.split(',')[0]?.trim() || 'daily life'}.`,
        `Understanding ${word.word} can enhance your skills.`,
        `In various contexts, ${word.word} plays a crucial role.`,
      ];
      const newText = templates[Math.floor(Math.random() * templates.length)];

      // Update the specific example
      word.examples[exampleIndex] = {
        text: newText,
        regenerationCount: example.regenerationCount + 1,
        isManuallyEdited: false,
      };
    } else if (data.examples) {
      // Manual edit - convert string array to object array
      word.examples = data.examples.map((text, i) => {
        const existingExample = word.examples[i] || {};
        return {
          text: typeof text === 'string' ? text : text.text,
          regenerationCount: existingExample.regenerationCount || 0,
          isManuallyEdited: true,
        };
      });
    }

    if (data.word) {
      word.word = data.word;
    }

    word.updatedAt = now.toISOString();

    return word;
  }

  async deleteWord(wordId) {
    await delay();

    const wordIndex = mockData.words.findIndex(
      (w) => w.wordId === wordId && w.userId === this.userId
    );

    if (wordIndex === -1) {
      throw {
        response: {
          status: 404,
          data: { message: 'Word not found' },
        },
      };
    }

    mockData.words.splice(wordIndex, 1);

    return { message: 'Word deleted successfully' };
  }

  async generateExercise(maxWords = 5, maxSentences = 10) {
    await delay();

    return {
      exerciseId: 'EX#mock',
      story: [
        'I went to the _____ to practice my skills.',
        'The _____ was very helpful and knowledgeable.',
        'I felt _____ after completing the challenging task.'
      ],
      words: [
        {
          wordId: 'WORD#1',
          word: 'library',
          translation: { text: 'библиотека', targetLanguage: 'ru' },
          blankIndex: 0
        },
        {
          wordId: 'WORD#2',
          word: 'instructor',
          translation: { text: 'инструктор', targetLanguage: 'ru' },
          blankIndex: 1
        },
        {
          wordId: 'WORD#3',
          word: 'accomplished',
          translation: { text: 'успешный', targetLanguage: 'ru' },
          blankIndex: 2
        }
      ],
      completedStory: [
        'I went to the library to practice my skills.',
        'The instructor was very helpful and knowledgeable.',
        'I felt accomplished after completing the challenging task.'
      ],
      createdAt: new Date().toISOString()
    };
  }

  // ========================================
  // Utility Methods
  // ========================================

  setUserId(userId) {
    this.userId = userId;
  }

  async submitFeedback(content) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return { feedbackId: 'mock-feedback-id', message: 'Feedback submitted successfully' };
  }

  setAuthToken(token) {
    // Mock implementation - no-op
  }

  clearAuthToken() {
    // Mock implementation - no-op
  }
}

// Export singleton instance
const mockApiClient = new MockApiClient();
export default mockApiClient;
